import { Box, FormControl, FormHelperText, Switch, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import type { useFormik } from 'formik';
import type { DateTime } from 'luxon';
import type { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { DialogForm } from '~/components/dialogs/components';
import { DurationSlider } from '~/components/inputs/DurationSlider/DurationSlider';

// Used as mutation input for create/update
export interface EventMutationValues {
  includeInPlayoutReporting: boolean;
  name: string;
  sponsorStartTime: string | null;
  sponsorStopTime: string | null;
  start: string;
  stop: string;
}

// Used by Formik
export interface EventFormValues {
  includeInPlayoutReporting: boolean;
  name: string;
  sponsorStartTime: string;
  sponsorStopTime: string;
  start: DateTime;
  stop: DateTime;
}

export interface EventFormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  /** Fields rendered above the event fields. */
  children?: ReactNode;
  formik: ReturnType<typeof useFormik<EventFormValues>>;
  synced?: boolean;
}

export const EventForm = ({ children, formik, synced, ...props }: EventFormProps) => {
  const { t } = useTranslation();
  const syncedTooltip = synced
    ? t('pages.event_types.events_tab.edit_event_dialog.synced_field_tooltip')
    : '';

  return (
    <DialogForm onSubmit={formik.handleSubmit} {...props}>
      {children}
      <Tooltip title={syncedTooltip}>
        <label>
          <span className="label required">{t('common.labels.name')}</span>
          <TextField
            autoFocus={!synced}
            disabled={synced}
            error={formik.touched.name && !!formik.errors.name}
            fullWidth
            helperText={(formik.touched.name && formik.errors.name) || ' '}
            name="name"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.name}
          />
        </label>
      </Tooltip>
      <Tooltip title={syncedTooltip}>
        <label>
          <span className="label required">
            {t('pages.event_types.events_tab.new_event_dialog.date_time_label')}
          </span>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <FormControl sx={{ mt: 1, width: '50%' }} variant="outlined">
              <DateTimePicker
                disabled={synced}
                format={'EEE, ff'}
                onChange={(value) => formik.setFieldValue('start', value)}
                value={formik.values.start}
                timeSteps={{ minutes: 15 }}
                skipDisabled={true}
                thresholdToRenderTimeInASingleColumn={200}
              />
              {formik.errors.start ? (
                <FormHelperText error>{formik.errors.start}</FormHelperText>
              ) : (
                <FormHelperText>
                  {t('pages.event_types.events_tab.new_event_dialog.start_helper')}
                </FormHelperText>
              )}
            </FormControl>
            <Typography>{t('pages.event_types.events_tab.new_event_dialog.to')}</Typography>
            <FormControl sx={{ mt: 1, width: '50%' }} variant="outlined">
              <DateTimePicker
                disabled={synced}
                format={'EEE, ff'}
                onChange={(value) => formik.setFieldValue('stop', value)}
                value={formik.values.stop}
                timeSteps={{ minutes: 15 }}
                skipDisabled={true}
                thresholdToRenderTimeInASingleColumn={200}
              />
              {formik.errors.stop ? (
                <FormHelperText error>{formik.errors.stop}</FormHelperText>
              ) : (
                <FormHelperText>
                  {t('pages.event_types.events_tab.new_event_dialog.stop_helper')}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </label>
      </Tooltip>
      <Box component="label">
        <Box className="label">
          <span>
            {t('pages.event_types.events_tab.edit_event_dialog.include_in_playout_reporting.label')}
          </span>
          <FormHelperText>
            {t(
              'pages.event_types.events_tab.edit_event_dialog.include_in_playout_reporting.description',
            )}
          </FormHelperText>
        </Box>
        <Switch
          checked={formik.values.includeInPlayoutReporting}
          disabled={formik.isSubmitting}
          name="includeInPlayoutReporting"
          onChange={(event) => {
            formik.handleChange(event);
            // The overrides only shape the reporting window.
            if (!event.target.checked) {
              void formik.setFieldValue('sponsorStartTime', '');
              void formik.setFieldValue('sponsorStopTime', '');
            }
          }}
        />
      </Box>
      {formik.values.includeInPlayoutReporting && (
        <>
          <Box component="label">
            <Box className="label">
              <span>
                {t('pages.event_types.events_tab.edit_event_dialog.sponsor_start_time.label')}
              </span>
              <FormHelperText>
                {t('pages.event_types.events_tab.edit_event_dialog.sponsor_start_time.description')}
              </FormHelperText>
            </Box>
            <DurationSlider
              disabled={formik.isSubmitting}
              max={{ hours: 5 }}
              min={{ minutes: 0 }}
              name="sponsorStartTime"
              step={{ minutes: 15 }}
              value={formik.values.sponsorStartTime}
              onChange={formik.handleChange}
            />
          </Box>
          <Box component="label">
            <Box className="label">
              <span>
                {t('pages.event_types.events_tab.edit_event_dialog.sponsor_stop_time.label')}
              </span>
              <FormHelperText>
                {t('pages.event_types.events_tab.edit_event_dialog.sponsor_stop_time.description')}
              </FormHelperText>
            </Box>
            <DurationSlider
              disabled={formik.isSubmitting}
              max={{ hours: 5 }}
              min={{ minutes: 0 }}
              name="sponsorStopTime"
              step={{ minutes: 15 }}
              value={formik.values.sponsorStopTime}
              onChange={formik.handleChange}
            />
          </Box>
        </>
      )}
    </DialogForm>
  );
};
